import React from "react";
import styled from "styled-components";
import {
  brand_orange_deep,
  brand_gradient,
  narrowScreenMediaQuery,
} from "../../constants/styles";

function CurriculumExam({ exam, curriculumProgressPercent }) {
  if (!exam || !exam.available) return null;

  const eligible = !!exam.eligible_to_take;
  const passed = !!exam.passed;
  const needsRemedial = !!exam.needs_remedial && !passed;

  const ctaLabel = passed
    ? "Tinjau Hasil →"
    : needsRemedial
      ? "Ujian Remedial →"
      : "Mulai Ujian →";

  const ctaUrl = passed
    ? exam.take_exam_url
    : needsRemedial
      ? exam.remedial_url || exam.take_exam_url
      : exam.take_exam_url;

  const showProgressGate =
    !eligible && typeof curriculumProgressPercent === "number";

  return (
    <Card aria-labelledby="curriculum-exam">
      <Eyebrow>Ujian Sertifikasi</Eyebrow>
      <Heading id="curriculum-exam">Uji Pemahaman</Heading>
      <MetaList>
        <MetaRow>
          <MetaKey>Pertanyaan</MetaKey>
          <MetaVal>{exam.total_questions || 0}</MetaVal>
        </MetaRow>
        {exam.exam_time_minutes ? (
          <MetaRow>
            <MetaKey>Durasi</MetaKey>
            <MetaVal>{exam.exam_time_minutes} menit</MetaVal>
          </MetaRow>
        ) : null}
        {exam.exam_minimum_score ? (
          <MetaRow>
            <MetaKey>Min. skor</MetaKey>
            <MetaVal>{exam.exam_minimum_score}</MetaVal>
          </MetaRow>
        ) : null}
      </MetaList>

      {exam.last_result && (
        <Result $passed={passed}>
          Skor terakhir: <strong>{exam.last_result.score}</strong> —{" "}
          {passed ? "Lulus" : "Belum Lulus"}
        </Result>
      )}

      {!eligible && !exam.last_result && (
        <Hint>
          Selesaikan seluruh materi pada modul ini untuk membuka ujian.
        </Hint>
      )}

      {showProgressGate && (
        <GateProgress>
          <GateBar
            role="progressbar"
            aria-valuenow={curriculumProgressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <GateFill style={{ width: `${curriculumProgressPercent}%` }} />
          </GateBar>
          <GateText>{curriculumProgressPercent}% siap</GateText>
        </GateProgress>
      )}

      {ctaUrl && eligible && (
        <PrimaryButton href={ctaUrl}>{ctaLabel}</PrimaryButton>
      )}
      {!eligible && (
        <DisabledButton aria-disabled="true" tabIndex={-1}>
          🔒 Kunci — Selesaikan materi
        </DisabledButton>
      )}
    </Card>
  );
}

export default React.memo(CurriculumExam);

const Card = styled.div`
  background: linear-gradient(135deg, #fff7ed, #ffedd5);
  border: 1px solid #fed7aa;
  border-radius: 20px;
  padding: 24px 26px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  [data-theme="dark"] & {
    background: rgba(216, 89, 7, 0.08);
    border-color: rgba(216, 89, 7, 0.32);
  }

  @media ${narrowScreenMediaQuery} {
    padding: 22px 18px;
  }
`;

const Eyebrow = styled.span`
  display: inline-block;
  width: fit-content;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${brand_gradient};
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  font-size: 22px;
  font-weight: 800;
  margin: 0;
  color: ${brand_orange_deep};
  line-height: 1.25;
`;

const MetaList = styled.dl`
  margin: 4px 0 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px 12px;
  font-size: 13px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(216, 89, 7, 0.25);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const MetaKey = styled.dt`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const MetaVal = styled.dd`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  font-variant-numeric: tabular-nums;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #6b7280;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const Result = styled.div`
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 14px;
  background: ${({ $passed }) =>
    $passed ? "rgba(16,185,129,0.12)" : "rgba(220,38,38,0.12)"};
  color: ${({ $passed }) => ($passed ? "#047857" : "#b91c1c")};

  strong {
    font-size: 16px;
  }
`;

const GateProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const GateBar = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: rgba(216, 89, 7, 0.15);
  overflow: hidden;
`;

const GateFill = styled.div`
  height: 100%;
  background: ${brand_gradient};
`;

const GateText = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${brand_orange_deep};
  font-variant-numeric: tabular-nums;
`;

const PrimaryButton = styled.a`
  display: block;
  width: 100%;
  text-align: center;
  padding: 14px 26px;
  border-radius: 999px;
  background: ${brand_orange_deep};
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  text-decoration: none;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition:
    filter 150ms ease,
    transform 100ms ease;
  &:hover {
    filter: brightness(0.95);
    transform: translateY(-1px);
    color: #fff;
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
`;

const DisabledButton = styled.span`
  display: block;
  width: 100%;
  text-align: center;
  padding: 14px 26px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  color: #6b7280;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: not-allowed;
`;
